import { ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Switch } from '@reflex/ui/components/ui/switch';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { Label } from '@reflex/ui/components/ui/label';
import type { AdminAgentModelEntry, AdminLaunchableAgentEntry } from '@reflex/shared';

export type PolicyScope = 'org';

export interface AgentPolicyCardHeaderProps {
  entry: AdminAgentModelEntry;
  launchable: AdminLaunchableAgentEntry | undefined;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  enabledModelsCount: number;
  totalDiscoveredModels: number;
  isAgentTogglePending: boolean;
  onToggleAvailability: (next: boolean) => void;
  readOnly?: boolean;
}

export function AgentPolicyCardHeader({
  entry,
  launchable,
  isExpanded,
  onToggleExpanded,
  enabledModelsCount,
  totalDiscoveredModels,
  isAgentTogglePending,
  onToggleAvailability,
  readOnly = false,
}: AgentPolicyCardHeaderProps) {
  const agentEffectiveEnabled = launchable?.effectiveEnabled ?? true;
  const { discoveryStatus, providers } = entry;

  return (
    <div className="p-3 flex items-center gap-2.5">
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 shrink-0"
        onClick={onToggleExpanded}
        aria-label={
          isExpanded
            ? `Hide ${entry.displayName} configuration`
            : `Show ${entry.displayName} configuration`
        }
        aria-expanded={isExpanded}
      >
        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </Button>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <PanelTitle as="h2" size="sm">
            {entry.displayName}
          </PanelTitle>
          {!agentEffectiveEnabled && (
            <Badge variant="destructive" className="text-2xs">
              disabled
            </Badge>
          )}
          <Badge variant="outline" className="text-2xs">
            {providers.length} provider{providers.length !== 1 ? 's' : ''}
          </Badge>
          {discoveryStatus === 'ready' && (
            <Badge variant="outline" className="text-2xs">
              {enabledModelsCount} / {totalDiscoveredModels} models
            </Badge>
          )}
          {discoveryStatus === 'pending' && (
            <Badge variant="secondary" className="text-2xs">
              no models available
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <Label className="text-xs text-muted-foreground max-sm:sr-only">Available</Label>
        <Switch
          checked={agentEffectiveEnabled}
          onCheckedChange={onToggleAvailability}
          disabled={readOnly || isAgentTogglePending}
          aria-label={`Toggle ${entry.displayName} availability`}
        />
      </div>
    </div>
  );
}
